/**
 * Reusable plan config form. Same structure as AdminPlanConfig.
 * Used by AdminPlanConfig (plan edit) and SubscriptionConfigModal (subscription config override).
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import MultiSelectDropdown from '../../components/layout/MultiSelectDropdown1';
import ObjectPermissionMatrix from '../../components/common/ObjectPermissionMatrix';
import {
  ADMIN_UI_LOCKED_ALLOWED_OBJECT_KEYS,
  allowedObjectsSelectedForPlanUi,
  getPackageRequiredObjectNames,
  getStandardTabPackageKey,
  isStandardTabNotGatedByAllowedObjects,
  isStandardTabPackageRequired,
  isAlwaysMigrateObject,
  isExcludedFromSearchViewObjectPicklist,
  isSearchViewCustomObjectKey,
  mergeAlwaysIncludedAllowedObjects,
  mergeTenantCustomIntoSearchViewPicklist,
  SETUP_OPTIONS,
  STANDARD_OBJECT_OPTIONS,
  getDisabledPackageObjectDenySet,
  stripDisabledPackageObjects,
  splitSearchViewPicklistByTenantSchema,
} from './AdminPlanConfigConstants';
import { isAdminModuleFullPlanEditor } from '../utils/adminSession';
import StorageLimitsFields from './StorageLimitsFields';

const PlanConfigForm = ({
  config,
  onChange,
  disabled,
  standardPicklistOptions,
  leadConversionPicklistOptions,
  onPackageToggle,
  standardTabs = [],
  standardObjects = [],
  /** Tenant object_config rows (object_name, is_standard) for subscription/company override; enables true custom vs standard split */
  tenantObjectConfigRows,
}) => {
  const packageLockedObjectKeys = useMemo(
    () => getPackageRequiredObjectNames(standardObjects, config.enabledPackages ?? []),
    [standardObjects, config.enabledPackages]
  );

  useEffect(() => {
    const raw = config.allowedObjectsPermission ?? config.objectManager?.allowedObjects ?? [];
    const next = stripDisabledPackageObjects(config.enabledPackages ?? [], raw);
    if (next.length === raw.length && next.every((x, i) => x === raw[i])) return;
    onChange(prev => {
      const merged = mergeAlwaysIncludedAllowedObjects(next);
      const lc = prev.leadConversionMapping?.allowedObjects ?? ['account', 'contact', 'opportunity'];
      const nextLc = lc.filter(o => merged.includes(o));
      const resolved =
        nextLc.length > 0
          ? nextLc
          : ['account', 'contact', 'opportunity'].filter(o => merged.includes(o));
      return {
        ...prev,
        allowedObjectsPermission: merged,
        objectManager: { ...prev.objectManager, allowedObjects: merged },
        leadConversionMapping: { ...prev.leadConversionMapping, allowedObjects: resolved },
      };
    });
  }, [config.enabledPackages, config.allowedObjectsPermission, config.objectManager?.allowedObjects, onChange]);

  const handleAllowedObjectsPermissionChange = (values) => {
    onChange((prev) => {
      const locked = getPackageRequiredObjectNames(standardObjects, prev.enabledPackages ?? []);
      const merged = mergeAlwaysIncludedAllowedObjects([...new Set([...locked, ...(values ?? [])])]);
      const currentLeadConversion = prev.leadConversionMapping?.allowedObjects ?? ['account', 'contact', 'opportunity'];
      const nextLeadConversion = currentLeadConversion.filter((o) => merged.includes(o));
      const resolved =
        nextLeadConversion.length > 0
          ? nextLeadConversion
          : ['account', 'contact', 'opportunity'].filter((o) => merged.includes(o));
      return {
        ...prev,
        allowedObjectsPermission: merged,
        objectManager: { ...prev.objectManager, allowedObjects: merged },
        leadConversionMapping: { ...prev.leadConversionMapping, allowedObjects: resolved },
      };
    });
  };

  const stdOptions = standardPicklistOptions ?? STANDARD_OBJECT_OPTIONS.map((o) => ({ key: o.name, value: o.label }));
  const packageDenySet = useMemo(
    () => getDisabledPackageObjectDenySet(config.enabledPackages),
    [config.enabledPackages]
  );
  /** Include any selected allowed-object values not in stdOptions (e.g. from subscription) so they show with a label, not [Unknown]. Exclude user, role, password_reset (always migrated). Omit package objects when that package is off. */
  const allowedObjectsPicklistOptions = useMemo(() => {
    const base = (stdOptions || [])
      .filter((o) => !isAlwaysMigrateObject(o.key))
      .filter((o) => !packageDenySet.has(String(o.key || '').toLowerCase().trim()));
    const selected = config.allowedObjectsPermission ?? config.objectManager?.allowedObjects ?? [];
    const keySet = new Set(base.map((o) => o.key));
    const extra = selected
      .filter(
        k =>
          k &&
          !isAlwaysMigrateObject(k) &&
          !keySet.has(k) &&
          !packageDenySet.has(String(k).toLowerCase().trim())
      )
      .map((k) => ({ key: k, value: (k || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim() || k }));
    return extra.length > 0 ? [...base, ...extra] : base;
  }, [
    stdOptions,
    config.allowedObjectsPermission,
    config.objectManager?.allowedObjects,
    packageDenySet,
  ]);
  const leadOptions = leadConversionPicklistOptions ?? allowedObjectsPicklistOptions.filter((o) => (config.allowedObjectsPermission ?? []).includes(o.key));

  const standardCatalogKeySet = useMemo(
    () => new Set((stdOptions || []).filter((o) => !isAlwaysMigrateObject(o.key)).map((o) => o.key)),
    [stdOptions]
  );
  const searchViewLeadOptions = useMemo(
    () => leadOptions.filter((o) => o?.key && !isExcludedFromSearchViewObjectPicklist(o.key)),
    [leadOptions]
  );
  const { standard: searchViewStandardPicklist, custom: searchViewCustomPicklistRaw } = useMemo(
    () => splitSearchViewPicklistByTenantSchema(searchViewLeadOptions, standardCatalogKeySet, tenantObjectConfigRows),
    [searchViewLeadOptions, standardCatalogKeySet, tenantObjectConfigRows]
  );
  const searchViewCustomPicklist = useMemo(
    () => mergeTenantCustomIntoSearchViewPicklist(searchViewCustomPicklistRaw, tenantObjectConfigRows),
    [searchViewCustomPicklistRaw, tenantObjectConfigRows]
  );
  const searchViewAllowed = config.searchView?.allowedObjects;
  const searchViewSelectedStandard = useMemo(
    () =>
      (searchViewAllowed ?? []).filter(
        (k) => k && !isSearchViewCustomObjectKey(k, standardCatalogKeySet, tenantObjectConfigRows)
      ),
    [searchViewAllowed, standardCatalogKeySet, tenantObjectConfigRows]
  );
  const searchViewSelectedCustom = useMemo(
    () =>
      (searchViewAllowed ?? []).filter((k) =>
        isSearchViewCustomObjectKey(k, standardCatalogKeySet, tenantObjectConfigRows)
      ),
    [searchViewAllowed, standardCatalogKeySet, tenantObjectConfigRows]
  );

  const searchViewsDebugSig = useMemo(
    () =>
      JSON.stringify({
        allowed: [...(config.allowedObjectsPermission ?? [])].map((x) => String(x)).sort(),
        lead: searchViewLeadOptions.map((o) => o.key).sort(),
        tenantRowCount: Array.isArray(tenantObjectConfigRows) ? tenantObjectConfigRows.length : -1,
        tenantCustomNames: (Array.isArray(tenantObjectConfigRows) ? tenantObjectConfigRows : [])
          .filter((r) => r?.is_standard === false)
          .map((r) => r.object_name)
          .sort(),
        std: searchViewStandardPicklist.map((o) => o.key).sort(),
        cust: searchViewCustomPicklist.map((o) => o.key).sort(),
      }),
    [
      config.allowedObjectsPermission,
      searchViewLeadOptions,
      tenantObjectConfigRows,
      searchViewStandardPicklist,
      searchViewCustomPicklist,
    ]
  );

  const lastSearchViewsLog = useRef('');
  useEffect(() => {
    if (lastSearchViewsLog.current === searchViewsDebugSig) return;
    lastSearchViewsLog.current = searchViewsDebugSig;

    const tenantRows = Array.isArray(tenantObjectConfigRows) ? tenantObjectConfigRows : [];
    const customNamesInSchema = tenantRows
      .filter((r) => r?.object_name && r.is_standard === false)
      .map((r) => r.object_name);

    let customDropdownWhy =
      searchViewCustomPicklist.length > 0
        ? null
        : tenantRows.length === 0
          ? 'No tenant schema rows passed to form (tenant API empty/failed, or not subscription context).'
          : customNamesInSchema.length === 0
            ? 'Tenant object_config has no rows with is_standard=false (no custom objects defined).'
            : 'Unexpected: tenant reports custom objects but merged picklist empty — check mergeTenantCustomIntoSearchViewPicklist.';

    console.info('[SearchViews:PlanConfigForm]', {
      tenantObjectConfigRowCount: tenantRows.length,
      customObjectNamesInTenantSchema: customNamesInSchema,
      searchViewLeadOptionKeys: searchViewLeadOptions.map((o) => o.key),
      standardPicklistKeys: searchViewStandardPicklist.map((o) => o.key),
      customPicklistKeys: searchViewCustomPicklist.map((o) => o.key),
      customDropdownEmptyReason: customDropdownWhy,
    });
  }, [searchViewsDebugSig, config.allowedObjectsPermission, tenantObjectConfigRows, searchViewCustomPicklist, searchViewStandardPicklist, searchViewLeadOptions]);

  const standardTabPicklistOptions = useMemo(() => {
    const allowedObjectNames = config.allowedObjectsPermission ?? [];
    const enabledPackages = config.enabledPackages ?? [];
    const enabledPkgLc = new Set(enabledPackages.map(p => String(p || '').toLowerCase().trim()).filter(Boolean));
    const formatKey = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return standardTabs
      .filter((t) => {
        const tabPackage = getStandardTabPackageKey(t);
        const packageOk = !tabPackage || enabledPkgLc.has(tabPackage);
        if (!packageOk) return false;
        return (
          isStandardTabNotGatedByAllowedObjects(t) ||
          !t.object_name ||
          allowedObjectNames.includes(t.object_name)
        );
      })
      .map((t) => {
        const displayName = t.label || (t.name || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || t.name;
        const suffixParts = [t.object_name, t.config?.package].filter(Boolean).map(formatKey);
        const value = suffixParts.length > 0 ? `${displayName} (${suffixParts.join(', ')})` : displayName;
        return { key: t.id, value };
      });
  }, [standardTabs, config.allowedObjectsPermission, config.enabledPackages]);

  const packageTabIds = useMemo(() => {
    const pkgSet = new Set((config.enabledPackages ?? []).map((p) => String(p).toLowerCase().trim()));
    return (standardTabs || [])
      .filter((t) => {
        const pkg = getStandardTabPackageKey(t);
        return pkg && pkgSet.has(pkg) && isStandardTabPackageRequired(t);
      })
      .map((t) => t.id)
      .filter(Boolean);
  }, [standardTabs, config.enabledPackages]);

  const tabAllowedTabsSelected = useMemo(() => {
    const validIds = new Set(standardTabPicklistOptions.map((o) => o.key));
    const saved = config.tab?.allowedTabs ?? [];
    const merged = [...new Set([...packageTabIds, ...saved])].filter((id) => validIds.has(id));
    if (merged.length > 0) return merged;
    return standardTabPicklistOptions.map((o) => o.key);
  }, [config.tab?.allowedTabs, standardTabPicklistOptions, packageTabIds]);

  return (
    <>
      {isAdminModuleFullPlanEditor() && (
        <Row className="g-3">
          <Col xs={12}>
            <Card className="p-3">
              <h5>Setup</h5>
              <Form.Check
                type="switch"
                label="Show setup"
                checked={config.setup?.showSetup !== false}
                onChange={(e) => onChange((prev) => ({ ...prev, setup: { ...prev.setup, showSetup: e.target.checked } }))}
                disabled={disabled}
                className="mb-2"
              />
              <Form.Text className="d-block mb-0 text-muted small">
                When off, this subscription hides Setup for the app. You can turn it on here. The sections below stay available for editing.
              </Form.Text>
            </Card>
          </Col>
        </Row>
      )}

      <Row className="g-3">
        <Col md={6}>
          <Card className="p-3">
            <h5>Packages</h5>
            <Form.Text className="d-block mb-2 text-muted small">
              Enable packages for this plan. Standard objects that belong to a package only appear in the list below when that package is enabled.
              You can enable more than one click-to-call / CTI package when needed.
            </Form.Text>
            <Form.Check
              type="switch"
              id="config-package-watconnect"
              label="WatConnect"
              checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('watconnect')}
              onChange={(e) => onPackageToggle && onPackageToggle('watconnect', e.target.checked)}
              disabled={disabled || !onPackageToggle}
            />
            <Form.Text className="text-muted small d-block mt-1">
              With WatConnect enabled, also select <strong>Watconnect Ads Settings</strong> under Allowed Objects to enable Meta Lead Ads mapping.
            </Form.Text>
            <Form.Check
              type="switch"
              id="config-package-watconnect-email-campaign"
              className="mt-2"
              label="Email Campaign"
              checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('email_campaign')}
              onChange={(e) => onPackageToggle && onPackageToggle('email_campaign', e.target.checked)}
              disabled={disabled || !onPackageToggle}
            />
            <Form.Text className="text-muted small d-block mt-1">
              When enabled, users can send email from list view; email_campaign and email_campaign_member objects are available.
            </Form.Text>
            <Form.Check
              type="switch"
              id="config-package-visit-manager"
              className="mt-2"
              label="Visit Manager"
              checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('visit_manager')}
              onChange={(e) => onPackageToggle && onPackageToggle('visit_manager', e.target.checked)}
              disabled={disabled || !onPackageToggle}
            />
            <Form.Text className="text-muted small d-block mt-1">
              When enabled, Visit Plan, Visit Plan Template, Visit Stop, Visit Stop Template, and Visit Template objects and tabs are selected by default; visit APIs and migration include them plus visit_mobile_config.
            </Form.Text>
            <Form.Check
              type="switch"
              id="config-package-real-estate"
              className="mt-2"
              label="Real Estate"
              checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('real_estate')}
              onChange={(e) => onPackageToggle && onPackageToggle('real_estate', e.target.checked)}
              disabled={disabled || !onPackageToggle}
            />
            <Form.Text className="text-muted small d-block mt-1">
              When enabled, Property, Floor, Unit, and Property Interest objects and tabs are selected by default. Contract stays a core CRM object.
            </Form.Text>
            <Form.Check
              type="switch"
              id="config-package-telecmi"
              className="mt-3"
              label="TeleCMI (click-to-call)"
              checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('telecmi')}
              onChange={(e) => onPackageToggle && onPackageToggle('telecmi', e.target.checked)}
              disabled={disabled || !onPackageToggle}
            />
            <Form.Check
              type="switch"
              id="config-package-callyzer"
              className="mt-2"
              label="Callyzer"
              checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('callyzer')}
              onChange={(e) => onPackageToggle && onPackageToggle('callyzer', e.target.checked)}
              disabled={disabled || !onPackageToggle}
            />
            <Form.Check
              type="switch"
              id="config-package-vgrow-dialer"
              className="mt-2"
              label="zemneo Dialer"
              checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('vgrow_dialer')}
              onChange={(e) => onPackageToggle && onPackageToggle('vgrow_dialer', e.target.checked)}
              disabled={disabled || !onPackageToggle}
            />
            <Form.Check
              type="switch"
              id="config-package-mis"
              className="mt-2"
              label="MIS Integration"
              checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('mis')}
              onChange={(e) => onPackageToggle && onPackageToggle('mis', e.target.checked)}
              disabled={disabled || !onPackageToggle}
            />


            <Form.Text className="text-muted small d-block mt-1">
              TeleCMI: Setup → TeleCMI, MIS Integration: Setup → MIS Integration and header softphone. Callyzer: Callyzer click-to-call. zemneo Dialer: zemneo dialer integration. Any combination can be on for this plan or subscription.
            </Form.Text>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="p-3">
            <h5>Allowed Objects Permission</h5>
            <Form.Text className="d-block mb-2 text-muted small">
              Objects the user can access. Only these objects are visible in tabs and configurable (with or without Setup). If user has no Setup, they still see and use only these objects.
            </Form.Text>
            <MultiSelectDropdown
              selectedValues={allowedObjectsSelectedForPlanUi(
                config.enabledPackages,
                config.allowedObjectsPermission ?? config.objectManager?.allowedObjects ?? []
              )}
              picklistValues={allowedObjectsPicklistOptions}
              onChange={handleAllowedObjectsPermissionChange}
              disabled={disabled}
              lockedKeys={[...packageLockedObjectKeys, ...ADMIN_UI_LOCKED_ALLOWED_OBJECT_KEYS]}
              placeholder="Select objects user can access"
            />
            <Form.Text className="text-muted small d-block mt-1">
              Objects required by an enabled package are selected automatically and cannot be cleared while that package is on. Activity, Task, Note, Email log, and Attachment are always allowed and stay selected. Product, Product price, User, Role, and Password reset are always included in migration and are not shown here.
            </Form.Text>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col xs={12}>
          <Card className="p-3">
            <h5>Setup Configuration</h5>
            <Form.Text className="d-block mb-3 text-muted small">
              Configure Setup Menu first (which items appear in Setup). Then Tab, Field, Layout, Workflow (if allowed), Scoring Rule, Assignment Rule, Web Forms, Search Views, Lead Conversion and Migration, Object Manager, and Object Permissions. Report and Dashboard are configured separately below (not part of Setup).
            </Form.Text>
            <Row className="g-3">
              <Col md={6}>
                <Card className="p-3 bg-light">
                  <h6 className="mb-2">Setup Menu</h6>
                  <Form.Group className="mb-2">
                    <Form.Label>Mode</Form.Label>
                    <Form.Select
                      value={config.setup?.mode ?? 'allowlist'}
                      onChange={(e) => onChange((prev) => ({ ...prev, setup: { ...prev.setup, mode: e.target.value } }))}
                      disabled={disabled}
                    >
                      <option value="allowlist">allowlist</option>
                      <option value="denylist">denylist</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Allowed setup items</Form.Label>
                    <MultiSelectDropdown
                      picklistValues={SETUP_OPTIONS}
                      selectedValues={config.setup?.allowedItems ?? []}
                      onChange={(values) => onChange((prev) => ({ ...prev, setup: { ...prev.setup, allowedItems: values } }))}
                      disabled={disabled}
                      placeholder="Select setup items"
                    />
                  </Form.Group>
                  <Form.Text className="text-muted small">Which setup menu items are available. If Workflow Manager is not selected here, the Workflow section below is hidden and workflow is disabled.</Form.Text>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="p-3 bg-light">
                  <h6 className="mb-2">App Manager</h6>
                  <Form.Text className="d-block mb-2 text-muted small">−1 = unlimited.</Form.Text>
                  <Form.Group className="mb-2">
                    <Form.Label>Max Apps</Form.Label>
                    <Form.Control
                      type="number"
                      min={-1}
                      value={config.app?.maxApps ?? -1}
                      onChange={(e) => {
                        const v = e.target.value;
                        const num = v === '' ? -1 : parseInt(v, 10);
                        if (!Number.isFinite(num)) return;
                        onChange((prev) => ({ ...prev, app: { ...(prev.app || {}), maxApps: num } }));
                      }}
                      disabled={disabled}
                    />
                  </Form.Group>
                  <Form.Check
                    type="switch"
                    label="Allow Create App"
                    checked={config.app?.allowCreateApp !== false}
                    onChange={(e) => onChange((prev) => ({ ...prev, app: { ...(prev.app || {}), allowCreateApp: e.target.checked } }))}
                    disabled={disabled}
                  />
                </Card>
              </Col>
              <Col md={6}>
                <Card className="p-3 bg-light">
                  <h6 className="mb-2">Tab Configuration</h6>
                  {standardTabPicklistOptions.length > 0 && (
                    <Form.Group className="mb-2">
                      <Form.Label>Allowed Standard Tabs</Form.Label>
                      <MultiSelectDropdown
                        picklistValues={standardTabPicklistOptions}
                        selectedValues={tabAllowedTabsSelected}
                        onChange={(values) => {
                          const requiredSet = new Set(packageTabIds);
                          const merged = [...packageTabIds, ...values.filter((id) => !requiredSet.has(id))];
                          onChange((prev) => ({ ...prev, tab: { ...prev.tab, allowedTabs: merged } }));
                        }}
                        disabled={disabled}
                        placeholder="Select standard tabs..."
                      />
                      <Form.Text className="text-muted small">
                        {packageTabIds.length > 0
                          ? 'Tabs for enabled packages (e.g. WatConnect, Email Campaign) are always included and cannot be removed.'
                          : 'Shows object and package when set.'}
                      </Form.Text>
                    </Form.Group>
                  )}
                  <Form.Group className="mb-2">
                    <Form.Label>Max Tabs</Form.Label>
                    <Form.Control
                      type="number"
                      min={-1}
                      value={config.tab?.maxTabs ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        const num = v === '' ? 0 : parseInt(v, 10);
                        if (!Number.isFinite(num)) return;
                        onChange((prev) => ({ ...prev, tab: { ...prev.tab, maxTabs: num } }));
                      }}
                      disabled={disabled}
                    />
                    <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                  </Form.Group>
                  <Form.Check
                    type="switch"
                    label="Allow Create"
                    checked={!!config.tab?.allowCreate}
                    onChange={(e) => onChange((prev) => ({ ...prev, tab: { ...prev.tab, allowCreate: e.target.checked } }))}
                    disabled={disabled}
                  />
                </Card>
              </Col>
              <Col md={6}>
                <Card className="p-3 bg-light">
                  <h6 className="mb-2">Field Management</h6>
                  <Form.Group className="mb-2">
                    <Form.Label>Max Custom Fields</Form.Label>
                    <Form.Control
                      type="number"
                      value={config.field?.maxCustom ?? ''}
                      onChange={(e) => onChange((prev) => ({ ...prev, field: { ...prev.field, maxCustom: Number(e.target.value) || 0 } }))}
                      disabled={disabled}
                    />
                  </Form.Group>
                  <Form.Check
                    type="switch"
                    label="Allow Create"
                    checked={!!config.field?.allowCreate}
                    onChange={(e) => onChange((prev) => ({ ...prev, field: { ...prev.field, allowCreate: e.target.checked } }))}
                    disabled={disabled}
                  />
                  <Form.Check
                    type="switch"
                    label="Allow Standard Edit"
                    checked={!!config.field?.allowStandardEdit}
                    onChange={(e) => onChange((prev) => ({ ...prev, field: { ...prev.field, allowStandardEdit: e.target.checked } }))}
                    disabled={disabled}
                  />
                  <Form.Check
                    type="switch"
                    label="Allow Standard Delete"
                    checked={!!config.field?.allowStandardDelete}
                    onChange={(e) => onChange((prev) => ({ ...prev, field: { ...prev.field, allowStandardDelete: e.target.checked } }))}
                    disabled={disabled}
                  />
                  <Form.Check
                    type="switch"
                    label="Allow Edit Label"
                    checked={!!config.field?.allowEditLabel}
                    onChange={(e) => onChange((prev) => ({ ...prev, field: { ...prev.field, allowEditLabel: e.target.checked } }))}
                    disabled={disabled}
                  />
                </Card>
              </Col>
              <Col md={6}>
                <Card className="p-3 bg-light">
                  <h6 className="mb-2">Layout Settings</h6>
                  <Form.Group className="mb-2">
                    <Form.Label>Count Mode</Form.Label>
                    <Form.Select
                      value={config.layout?.countMode ?? 'pair'}
                      onChange={(e) => onChange((prev) => ({ ...prev, layout: { ...prev.layout, countMode: e.target.value } }))}
                      disabled={disabled}
                    >
                      <option value="pair">pair</option>
                      <option value="single">single</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Max Layouts</Form.Label>
                    <Form.Control
                      type="number"
                      min={-1}
                      value={config.layout?.maxLayouts ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        const num = v === '' ? 0 : parseInt(v, 10);
                        if (!Number.isFinite(num)) return;
                        onChange((prev) => ({ ...prev, layout: { ...prev.layout, maxLayouts: num } }));
                      }}
                      disabled={disabled}
                    />
                    <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                  </Form.Group>
                  <Form.Check
                    type="switch"
                    label="Allow Create"
                    checked={!!config.layout?.allowCreate}
                    onChange={(e) => onChange((prev) => ({ ...prev, layout: { ...prev.layout, allowCreate: e.target.checked } }))}
                    disabled={disabled}
                  />
                </Card>
              </Col>
              {(config.setup?.allowedItems ?? []).map((i) => String(i).toLowerCase()).includes('workflows') && (
                <Col md={6}>
                  <Card className="p-3 bg-light">
                    <h6 className="mb-2">Workflow</h6>
                    <Form.Text className="d-block mb-2 text-muted small">Workflow Manager in Setup Menu gives view access. Plan controls max count and create permission only.</Form.Text>
                    <Form.Group className="mb-2">
                      <Form.Label>Max Workflows</Form.Label>
                      <Form.Control
                        type="number"
                        min={-1}
                        value={config.workflow?.maxWorkflows ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          const num = v === '' ? 0 : parseInt(v, 10);
                          if (!Number.isFinite(num)) return;
                          onChange((prev) => ({ ...prev, workflow: { ...prev.workflow, maxWorkflows: num } }));
                        }}
                        disabled={disabled}
                      />
                      <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                    </Form.Group>
                    <Form.Check
                      type="switch"
                      label="Allow Create Workflow"
                      checked={!!config.workflow?.allowCreateWorkflow}
                      onChange={(e) => onChange((prev) => ({ ...prev, workflow: { ...prev.workflow, allowCreateWorkflow: e.target.checked } }))}
                      disabled={disabled}
                    />
                  </Card>
                </Col>
              )}
              <Col md={6}>
                <Card className="p-3 bg-light">
                  <h6 className="mb-2">Scoring Rules</h6>
                  <Form.Text className="d-block mb-2 text-muted small">Which objects can have scoring rules. Only objects in Allowed Objects Permission are listed.</Form.Text>
                  <Form.Check
                    type="switch"
                    label="Allow View Scoring Rules"
                    checked={!!config.scoringRule?.allowViewScoringRule}
                    onChange={(e) => onChange((prev) => ({ ...prev, scoringRule: { ...prev.scoringRule, allowViewScoringRule: e.target.checked } }))}
                    disabled={disabled}
                  />
                  <Form.Check
                    type="switch"
                    label="Allow Create / Edit Scoring Rules"
                    checked={!!config.scoringRule?.allowCreateScoringRule}
                    onChange={(e) => onChange((prev) => ({ ...prev, scoringRule: { ...prev.scoringRule, allowCreateScoringRule: e.target.checked } }))}
                    disabled={disabled}
                  />
                  <Form.Group className="mb-0 mt-2">
                    <Form.Label>Objects allowed for scoring rules</Form.Label>
                    <MultiSelectDropdown
                      picklistValues={leadOptions}
                      selectedValues={config.scoringRule?.allowedObjects ?? []}
                      onChange={(values) => onChange((prev) => ({ ...prev, scoringRule: { ...prev.scoringRule, allowedObjects: values } }))}
                      disabled={disabled}
                      placeholder="Select objects..."
                    />
                  </Form.Group>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="p-3 bg-light">
                  <h6 className="mb-2">Assignment Rules</h6>
                  <Form.Text className="d-block mb-2 text-muted small">Which objects can have assignment rules. Only objects in Allowed Objects Permission are listed.</Form.Text>
                  <Form.Check
                    type="switch"
                    label="Allow View Assignment Rules"
                    checked={!!config.assignmentRule?.allowViewAssignmentRule}
                    onChange={(e) => onChange((prev) => ({ ...prev, assignmentRule: { ...prev.assignmentRule, allowViewAssignmentRule: e.target.checked } }))}
                    disabled={disabled}
                  />
                  <Form.Check
                    type="switch"
                    label="Allow Create / Edit Assignment Rules"
                    checked={!!config.assignmentRule?.allowCreateAssignmentRule}
                    onChange={(e) => onChange((prev) => ({ ...prev, assignmentRule: { ...prev.assignmentRule, allowCreateAssignmentRule: e.target.checked } }))}
                    disabled={disabled}
                  />
                  <Form.Group className="mb-0 mt-2">
                    <Form.Label>Objects allowed for assignment rules</Form.Label>
                    <MultiSelectDropdown
                      picklistValues={leadOptions}
                      selectedValues={config.assignmentRule?.allowedObjects ?? []}
                      onChange={(values) => onChange((prev) => ({ ...prev, assignmentRule: { ...prev.assignmentRule, allowedObjects: values } }))}
                      disabled={disabled}
                      placeholder="Select objects..."
                    />
                  </Form.Group>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="p-3 bg-light">
                  <h6 className="mb-2">Web Forms</h6>
                  <Form.Text className="d-block mb-2 text-muted small">Which objects can have web forms. Only objects in Allowed Objects Permission are listed.</Form.Text>
                  <Form.Check
                    type="switch"
                    label="Allow View Web Forms"
                    checked={!!config.webform?.allowViewWebform}
                    onChange={(e) => onChange((prev) => ({ ...prev, webform: { ...prev.webform, allowViewWebform: e.target.checked } }))}
                    disabled={disabled}
                  />
                  <Form.Check
                    type="switch"
                    label="Allow Create / Edit Web Forms"
                    checked={!!config.webform?.allowCreateWebform}
                    onChange={(e) => onChange((prev) => ({ ...prev, webform: { ...prev.webform, allowCreateWebform: e.target.checked } }))}
                    disabled={disabled}
                  />
                  <Form.Group className="mb-0 mt-2">
                    <Form.Label>Objects allowed for web forms</Form.Label>
                    <MultiSelectDropdown
                      picklistValues={leadOptions}
                      selectedValues={config.webform?.allowedObjects ?? []}
                      onChange={(values) => onChange((prev) => ({ ...prev, webform: { ...prev.webform, allowedObjects: values } }))}
                      disabled={disabled}
                      placeholder="Select objects..."
                    />
                  </Form.Group>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="p-3 bg-light">
                  <h6 className="mb-2">Search Views</h6>
                  <Form.Text className="d-block mb-2 text-muted small">
                    Standard objects follow Allowed Objects Permission. Custom objects are taken from the tenant schema (
                    <code className="small">is_standard = false</code>) and do not need to be in Allowed Objects Permission. Always-migrated objects are omitted.
                  </Form.Text>
                  <Form.Check
                    type="switch"
                    label="Allow View Search Views"
                    checked={!!config.searchView?.allowViewSearchView}
                    onChange={(e) => onChange((prev) => ({ ...prev, searchView: { ...prev.searchView, allowViewSearchView: e.target.checked } }))}
                    disabled={disabled}
                  />
                  <Form.Check
                    type="switch"
                    label="Allow Create / Edit Search Views"
                    checked={!!config.searchView?.allowCreateSearchView}
                    onChange={(e) => onChange((prev) => ({ ...prev, searchView: { ...prev.searchView, allowCreateSearchView: e.target.checked } }))}
                    disabled={disabled}
                  />
                  <Form.Group className="mb-0 mt-2">
                    <Form.Label>Standard objects</Form.Label>
                    {searchViewStandardPicklist.length > 0 ? (
                      <MultiSelectDropdown
                        picklistValues={searchViewStandardPicklist}
                        selectedValues={searchViewSelectedStandard}
                        onChange={(values) =>
                          onChange((prev) => {
                            const custom = (prev.searchView?.allowedObjects ?? []).filter((k) =>
                              isSearchViewCustomObjectKey(k, standardCatalogKeySet, tenantObjectConfigRows)
                            );
                            return {
                              ...prev,
                              searchView: { ...prev.searchView, allowedObjects: [...new Set([...(values ?? []), ...custom])] },
                            };
                          })
                        }
                        disabled={disabled}
                        placeholder="Select standard objects..."
                      />
                    ) : (
                      <Form.Text className="text-muted small d-block">
                        No standard objects in this list. Add objects under Allowed Objects Permission (Search Views only includes non–always-migrated names).
                      </Form.Text>
                    )}
                  </Form.Group>
                  <Form.Group className="mb-0 mt-2">
                    <Form.Label>Custom objects</Form.Label>
                    {searchViewCustomPicklist.length > 0 ? (
                      <MultiSelectDropdown
                        picklistValues={searchViewCustomPicklist}
                        selectedValues={searchViewSelectedCustom}
                        onChange={(values) =>
                          onChange((prev) => {
                            const std = (prev.searchView?.allowedObjects ?? []).filter(
                              (k) => !isSearchViewCustomObjectKey(k, standardCatalogKeySet, tenantObjectConfigRows)
                            );
                            return {
                              ...prev,
                              searchView: { ...prev.searchView, allowedObjects: [...new Set([...std, ...(values ?? [])])] },
                            };
                          })
                        }
                        disabled={disabled}
                        placeholder="Select custom objects..."
                      />
                    ) : (
                      <Form.Text className="text-muted small d-block">
                        No tenant custom objects (<code className="small">is_standard = false</code>) or tenant schema not loaded. See browser console{' '}
                        <code className="small">[SearchViews:PlanConfigForm]</code>.
                      </Form.Text>
                    )}
                  </Form.Group>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="p-3 bg-light">
                  <h6 className="mb-2">Lead Conversion and Migration</h6>
                  <Form.Check
                    type="switch"
                    label="Allow Mapping"
                    checked={!!config.leadConversionMapping?.allow}
                    onChange={(e) => onChange((prev) => ({ ...prev, leadConversionMapping: { ...prev.leadConversionMapping, allow: e.target.checked } }))}
                    disabled={disabled}
                  />
                  <Form.Check
                    type="switch"
                    label="Allow Custom Objects"
                    checked={!!config.leadConversionMapping?.allowCustomObject}
                    onChange={(e) => onChange((prev) => ({ ...prev, leadConversionMapping: { ...prev.leadConversionMapping, allowCustomObject: e.target.checked } }))}
                    disabled={disabled}
                    className="mt-2"
                  />
                  <Form.Check
                    type="switch"
                    label="Data Migration"
                    checked={!!config.migration?.allow}
                    onChange={(e) => onChange((prev) => ({ ...prev, migration: { ...prev.migration, allow: e.target.checked } }))}
                    disabled={disabled}
                    className="mt-2"
                  />
                  <Form.Group className="mb-2 mt-2">
                    <Form.Label>Mode</Form.Label>
                    <Form.Select
                      value={config.leadConversionMapping?.mode ?? 'allowlist'}
                      onChange={(e) => onChange((prev) => ({ ...prev, leadConversionMapping: { ...prev.leadConversionMapping, mode: e.target.value } }))}
                      disabled={disabled}
                    >
                      <option value="allowlist">allowlist</option>
                      <option value="denylist">denylist</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Max Objects</Form.Label>
                    <Form.Control
                      type="number"
                      value={config.leadConversionMapping?.maxObjects ?? ''}
                      onChange={(e) => onChange((prev) => ({ ...prev, leadConversionMapping: { ...prev.leadConversionMapping, maxObjects: Number(e.target.value) || 0 } }))}
                      disabled={disabled}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Objects for lead conversion</Form.Label>
                    <MultiSelectDropdown
                      picklistValues={leadOptions}
                      selectedValues={config.leadConversionMapping?.allowedObjects ?? ['account', 'contact', 'opportunity']}
                      onChange={(values) => onChange((prev) => ({ ...prev, leadConversionMapping: { ...prev.leadConversionMapping, allowedObjects: values } }))}
                      disabled={disabled}
                      placeholder="Select objects..."
                    />
                  </Form.Group>
                  <Form.Text className="text-muted small">Choose which of the configured Allowed Objects Permission can be used for lead conversion. Default: Account, Contact, Opportunity.</Form.Text>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="p-3 bg-light">
                  <h6 className="mb-2">Object Manager</h6>
                  <Form.Text className="d-block mb-2 text-muted small">Uses Allowed Objects Permission above. Without Setup, users only see those objects (no add/remove fields or layout).</Form.Text>
                  <Form.Check
                    type="switch"
                    label="Allow Create"
                    checked={!!config.objectManager?.allow_create}
                    onChange={(e) => onChange((prev) => ({ ...prev, objectManager: { ...prev.objectManager, allow_create: e.target.checked } }))}
                    disabled={disabled}
                    className="mb-2"
                  />
                  <Form.Check
                    type="switch"
                    label="Enable Standard"
                    checked={!!config.objectManager?.enable_standard}
                    onChange={(e) => onChange((prev) => ({ ...prev, objectManager: { ...prev.objectManager, enable_standard: e.target.checked } }))}
                    disabled={disabled}
                    className="mb-2"
                  />
                </Card>
              </Col>
              <Col xs={12}>
                <Card className="p-3 bg-light">
                  <h6 className="mb-2">Object Permissions</h6>
                  <Form.Text className="d-block mb-2 text-muted small">
                    Define per-object permissions (Create, Read, Update, Delete, View All, Modify All). Same UI as role PermissionBulkEdit. Plan licenses inherit and can restrict further.
                  </Form.Text>
                  <ObjectPermissionMatrix
                    objects={stripDisabledPackageObjects(
                      config.enabledPackages,
                      config.allowedObjectsPermission ?? []
                    ).map((name) => {
                      const opt = stdOptions.find((o) => String(o.key).toLowerCase() === String(name).toLowerCase());
                      const displayLabel = opt?.value || (String(name).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || name);
                      return { object_name: name, object_display_name: displayLabel };
                    })}
                    permissions={config.objectPermissions ?? {}}
                    onChange={(objectPermissions) => onChange((prev) => ({ ...prev, objectPermissions }))}
                    disabled={disabled}
                    showSearch={true}
                  />
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col md={6}>
          <Card className="p-3">
            <h5>Report</h5>
            <Form.Text className="d-block mb-2 text-muted small">Report is not part of Setup; configure regardless of Setup.</Form.Text>
            <Form.Group className="mb-2">
              <Form.Label>Max Reports</Form.Label>
              <Form.Control
                type="number"
                min={-1}
                value={config.report?.maxReports ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  const num = v === '' ? 0 : parseInt(v, 10);
                  if (!Number.isFinite(num)) return;
                  onChange((prev) => ({ ...prev, report: { ...prev.report, maxReports: num } }));
                }}
                disabled={disabled}
              />
              <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
            </Form.Group>
            <Form.Check
              type="switch"
              label="Allow Create Report"
              checked={!!config.report?.allowCreateReport}
              onChange={(e) => onChange((prev) => ({ ...prev, report: { ...prev.report, allowCreateReport: e.target.checked } }))}
              disabled={disabled}
            />
          </Card>
        </Col>
        <Col md={6}>
          <Card className="p-3">
            <h5>Dashboard</h5>
            <Form.Text className="d-block mb-2 text-muted small">Dashboard is not part of Setup; configure regardless of Setup.</Form.Text>
            <Form.Group className="mb-2">
              <Form.Label>Max Dashboards</Form.Label>
              <Form.Control
                type="number"
                min={-1}
                value={config.dashboard?.maxDashboards ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  const num = v === '' ? 0 : parseInt(v, 10);
                  if (!Number.isFinite(num)) return;
                  onChange((prev) => ({ ...prev, dashboard: { ...prev.dashboard, maxDashboards: num } }));
                }}
                disabled={disabled}
              />
              <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Max assigned dashboards per user</Form.Label>
              <Form.Control
                type="number"
                min={1}
                value={config.dashboard?.maxAssignedDashboardsPerUser ?? 2}
                onChange={(e) => {
                  const v = e.target.value;
                  const num = v === '' ? 2 : parseInt(v, 10);
                  if (!Number.isFinite(num) || num < 1) return;
                  onChange((prev) => ({
                    ...prev,
                    dashboard: { ...prev.dashboard, maxAssignedDashboardsPerUser: num },
                  }));
                }}
                disabled={disabled}
              />
              <Form.Text className="text-muted small">
                How many dashboards a user can be assigned to across the org (default 2).
              </Form.Text>
            </Form.Group>
            <Form.Check
              type="switch"
              label="Allow Create Dashboard"
              checked={!!config.dashboard?.allowCreateDashboard}
              onChange={(e) => onChange((prev) => ({ ...prev, dashboard: { ...prev.dashboard, allowCreateDashboard: e.target.checked } }))}
              disabled={disabled}
            />
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col xs={12}>
          <StorageLimitsFields
            storage={config.storage}
            onChange={onChange}
            disabled={disabled}
            subtitle="Tenant-wide storage limits. On a subscription override, values here replace the plan defaults."
          />
        </Col>
      </Row>
    </>
  );
};

export default PlanConfigForm;
